import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  throwError,
  timeout,
  TimeoutError,
} from 'rxjs';
import { LoginResponse, RefreshTokenRequest, RefreshTokenResponse } from '../model/user.model';
import { environment } from '../environments/environment';
import { HttpService } from './http.service';
import { Store } from '@ngrx/store';
import { selectAuthToken } from '../store/auth/selectors/auth.selectors';
import { catchError, filter, map, share, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private httpService = inject(HttpService);
  private store = inject(Store);

  // Token refresh lock mechanism to prevent race conditions
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<RefreshTokenResponse | null>(null);

  // getCurrentUser(): Observable<User> {
  //   return this.httpService
  //     .get<User>(`${environment.endPoint}/user/users`, new HttpParams(), true)
  //     .pipe(
  //       tap( user => console.log(`User ${user}`)),
  //       timeout(10000), // 10 second timeout (same as logout)
  //       catchError((error) => {
  //         if (error.name === 'TimeoutError') {
  //           console.log('Auth check timed out');
  //         }
  //         return throwError(() => error);
  //       }),
  //     );
  // }

  /**
   * Logout user and revoke refresh token on server
   * Note: Even if server call fails, we clear local state for security
   * Includes 5-second timeout to prevent hanging on network issues
   */
  logout(): Observable<void> {
    return new Observable<void>((observer) => {
      // Get current refresh token from store
      this.store
        .select(selectAuthToken)
        .pipe(take(1))
        .subscribe((token) => {
          if (token?.refreshToken) {
            // Call backend to revoke refresh token with timeout
            const request: RefreshTokenRequest = {
              refreshToken: token.refreshToken,
            };

            this.httpService
              .post<{ message: string }>(
                `${environment.endPoint}/auth/logout`,
                request,
                false, // Send with auth token - backend needs to authenticate the logout request
              )
              .pipe(
                timeout(5000), // 5 second timeout for logout
                catchError((error) => {
                  // Handle timeout or other errors
                  if (error instanceof TimeoutError) {
                    console.error('Logout request timed out after 5 seconds');
                  } else {
                    console.error('Logout API call failed', error);
                  }
                  // Always return success - we'll clear local state anyway
                  return throwError(() => error);
                }),
              )
              .subscribe({
                next: () => {
                  console.log('Refresh token revoked on server');
                  localStorage.removeItem('auth');
                  observer.next();
                  observer.complete();
                },
                error: (err) => {
                  // Even if server call fails (timeout, 401, network error),
                  // clear local state for security and complete successfully
                  console.error('Logout API call failed, clearing local state anyway', err);
                  localStorage.removeItem('auth');
                  // Return the error to let effect handle it (e.g., forceLogout on 401)
                  observer.error(err);
                },
              });
          } else {
            // No refresh token, just clear local state
            localStorage.removeItem('auth');
            observer.next();
            observer.complete();
          }
        });
    });
  }

  /**
   * Login with WebAuthn passkey
   * Uses /webauthn-login endpoint which returns both access and refresh tokens
   */
  loginWithPasskey(credentials: {
    credentialId: string;
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    clientExtensions: string;
  }): Promise<LoginResponse> {
    return firstValueFrom(
      this.httpService.post<LoginResponse>(
        `${environment.endPoint}/webauthn-login`,
        credentials,
        true,
      ),
    );
  }

  /**
   * Refresh access token using refresh token
   * Called automatically when access token expires
   *
   * Uses a lock mechanism to prevent race conditions when multiple
   * concurrent requests try to refresh the token simultaneously.
   * Only one refresh request is made; others wait for its result.
   */
  refreshAccessToken(refreshToken: string): Observable<RefreshTokenResponse> {
    // If refresh is already in progress, wait for it to complete
    if (this.refreshTokenInProgress) {
      console.log('Token refresh already in progress, waiting...');
      return this.refreshTokenSubject.pipe(
        filter((result): result is RefreshTokenResponse => result !== null),
        take(1),
      );
    }

    // Start new refresh
    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);
    console.log('Starting token refresh...');

    const request: RefreshTokenRequest = { refreshToken };
    return this.httpService
      .post<RefreshTokenResponse>(
        `${environment.endPoint}/auth/refresh`,
        request,
        false, // Don't skip auth - we need the expired access token in header
      )
      .pipe(
        tap((response) => {
          console.log('Token refresh completed successfully');
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(response);
        }),
        catchError((error) => {
          console.error('Token refresh failed', error);
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(null);
          return throwError(() => error);
        }),
        // Share the same response with all waiting subscribers
        share(),
      );
  }

  registerWithPasskey(data: {
    userHandle: string;
    username: string;
    clientDataJSON: string;
    attestationObject: string;
    transports: string[];
    clientExtensions: string;
  }): Promise<{ message?: string; error?: string }> {
    return firstValueFrom(
      this.httpService.post<{ message?: string; error?: string }>(
        `${environment.endPoint}/signup`,
        data,
        true,
      ),
    );
  }
}
